/* eslint-disable no-param-reassign */
import axios from 'axios';
import { API_URL } from 'constant/app.config';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const baseURL = API_URL;
const axiosconfig = axios.create({
  baseURL,
});

const saveErrorLog = (error) => {
  try {
    const existingLogs = JSON.parse(localStorage.getItem('errorLogs')) || [];

    const newLog = {
      message: error.message,
      status: error?.response?.status,
      url: error?.config?.url,
      time: new Date().toISOString(),
    };

    existingLogs.push(newLog);

    localStorage.setItem('errorLogs', JSON.stringify(existingLogs));
  } catch (e) {
    console.error('Failed to save log', e);
  }
};

const forceLogout = (error) => {
  if (error?.response?.status === 401) {
    const logsBackup = localStorage.getItem('errorLogs');

    localStorage.clear();
    sessionStorage.clear();

    if (logsBackup) {
      localStorage.setItem('errorLogs', logsBackup);
      localStorage.setItem(
        'isexpired',
        'Your session has expired. Please log in again to continue.'
      );
    }

    // change this if your logout route differs
    window.location.replace('/');
  }
};

axiosconfig.interceptors.request.use((req) => {
  // ✅ DON'T overwrite headers
  req.headers = req.headers || {};

  req.headers.Authorization = localStorage.getItem('accesstoken');

  // ✅ Only set JSON for non-FormData
  if (!(req.data instanceof FormData)) {
    req.headers['Content-Type'] = 'application/json';
  }

  return req;
});

axiosconfig.interceptors.response.use(
  (response) => response,
  (error) => {
    saveErrorLog(error); // save all errors
    forceLogout(error); // logout only on 401
    return Promise.reject(error);
  }
);

const postApiCall = (url, params = {}) =>
  axiosconfig.post(url, params).then((res) => res);

const putApiCall = (url, params = {}) =>
  axiosconfig.put(url, params).then((res) => res);

const deleteApiCall = (url, paramData = '') => {
  const queryparams = {
    ...(paramData !== '' && { params: paramData }),
  };
  return axiosconfig
    .delete(url, {
      ...queryparams,
    })
    .then((res) => res);
};

const getApiCall = (url, paramData = '') => {
  const queryparams = {
    ...(paramData !== '' && { params: paramData }),
  };
  return axiosconfig
    .get(url, {
      ...queryparams,
    })
    .then((res) => res);
};

export const formatDateTime = (date, format = 'HH:mm:ss DD-MM-YYYY') => {
  if (!date) return '';
  return dayjs(date).format(format);
};

export const formatDateTimeSydney = (
  date,
  format = 'HH:mm:ss DD-MM-YYYY',
  tz = 'Australia/Sydney'
) => {
  if (!date) return '';
  return dayjs(date).tz(tz).format(format);
};

export const formatBasedOnUserType = (date, userType) => {
  if (!date) return '';

  const vendorFormat = 'HH:mm - DD-MM-YYYY';
  const buyerFormat = 'HH:mm:ss DD-MM-YYYY';
  const agentFormat = 'HH:mm:ss DD-MM-YYYY';

  return userType == 'Vendor'
    ? formatDateTime(date, vendorFormat)
    : userType == 'Agent'
    ? formatDateTime(date, agentFormat)
    : formatDateTime(date, buyerFormat);
};

export const dateformatBasedOnUserType = (date, userType) => {
  if (!date) return '';

  const vendorFormat = 'HH:mm DD-MM-YYYY';
  const agentFormat = 'HH:mm:ss DD-MM-YYYY';

  return userType == 'Vendor'
    ? formatDateTime(date, vendorFormat)
    : formatDateTime(date, agentFormat);
};

export const formatDepositDate = (rawDate) => {
  if (!rawDate) return '';

  try {
    const [timePart, datePart] = rawDate.toLowerCase().split(' - ');
    const time = dayjs(timePart, ['hh:mma']);
    const [day, mon, yr] = datePart.split(' ');
    const monthMap = {
      jan: '01',
      feb: '02',
      mar: '03',
      apr: '04',
      may: '05',
      jun: '06',
      jul: '07',
      aug: '08',
      sep: '09',
      oct: '10',
      nov: '11',
      dec: '12',
    };

    const month = monthMap[mon];
    const year = `20${yr}`;

    const combined = `${year}-${month}-${day}T${time.format('HH:mm:ss')}`;

    return dayjs(combined).format('HH:mm:ss DD-MM-YYYY');
  } catch (e) {
    return rawDate;
  }
};

export const offerWithdrawVendor = async (input) => {
  const response = await axios.post(`${API_URL}vendor/withdrawOffer`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  return response.data;
};
export const offerWithdrawFunc = async (input) => {
  const response = await axios.post(`${API_URL}buyer/withdrawOffer`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  return response.data;
};

export const addOtherDocuments = async (input) => {
  const response = await axios.post(
    `${API_URL}agent/addOtherDocuments`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const addPipelineOtherDocuments = async (input) => {
  const response = await axios.post(
    `${API_URL}agent/addPipelineOtherDocuments`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const discussWithAgent = async (input) => {
  const response = await axios.post(
    `${API_URL}vendor/discussWithAgent`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );
  return response.data;
};

export const vendorDashboardActivity = async (input) => {
  const response = await axios.post(`${API_URL}vendor/activity`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};
export const buyerDashboardActivity = async (input) => {
  const response = await axios.post(`${API_URL}buyer/activity`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};
export const stageTwoView = async (input) => {
  const response = await axios.post(
    `${API_URL}buyer/stageTwoAcceptLog`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );
  return response.data;
};
export const stageTwoCounter = async (input) => {
  const response = await axios.post(
    `${API_URL}buyer/counterOfferViewLog`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );
  return response.data;
};

export const dashboardActivity = async (input) => {
  const response = await axios.post(`${API_URL}agent/activity`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

const getContactDetails = async (page) => {
  // const start = (page - 1) * 10;
  const start = page ? (page === 0 ? 0 : (page - 1) * 10) : 0;
  // const start = page

  // console.log(start, "datapage");

  const response = await axios.get(
    `${API_URL}agent/getContact/All/${start}/10`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("ContactDetails", response.data);

  return response.data;
};

const getBuyerDetails = async (page) => {
  // const start = (page - 1) * 10;
  const start = page ? (page === 0 ? 0 : (page - 1) * 10) : 0;

  // const start = page
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  const response = await axios.get(
    `${API_URL}agent/getContact/Buyer/${start}/10`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("BuyerDetails", response.data);

  return response.data;
};
const getOwnerDetails = async (page, limit) => {
  // const start = (page - 1) * 10;
  const start = page ? (page === 0 ? 0 : (page - 1) * 10) : 0;

  // const start = page

  const response = await axios.get(
    `${API_URL}agent/getContact/Owner/${start}/10`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("ContactDetails", response.data);

  return response.data;
};

export const getOtherDocuments = async (params) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  const response = await axios.get(
    `${API_URL}agent/getOtherDocuments/${params?.tempId}`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("ContactPageCount", response.data);

  return response.data;
};

export const getPipelienOtherDocuments = async (params) => {
  const response = await axios.get(
    `${API_URL}agent/getPipelineOtherDocuments/${params?.tempId}/${params?.propertyId}`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );
  return response.data;
};
const getCount = async () => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  const response = await axios.get(`${API_URL}buyer/getDashboardCount`, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("ContactPageCount", response.data);

  return response.data;
};

const getBuyerIndividualData = async (
  propertyId,
  currentUrl,
  primaryUserType
) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  if (currentUrl?.includes('share') && primaryUserType != 'Buyer') {
    const response = await axios.get(
      `${API_URL}buyer/getIndividualPropertyShare/` + `${propertyId}`,
      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getBuyerIndividualData", response.data);

    return response.data;
  } else {
    const response = await axios.get(
      `${API_URL}buyer/getIndividualProperty/` + `${propertyId}`,
      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getBuyerIndividualData", response.data);

    return response.data;
  }
};

const getFollowRequest = async (propertyId, userType, isFollow) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  const input = {
    propertyId: propertyId,
    userType: userType,
    isFollow: isFollow,
  };
  // console.log(input, "input");
  const response = await axios.post(`${API_URL}buyer/makeWatchlist`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("getBuyerIndividualData", response.data);

  return response.data;
};

export const storeTagnames = async (input) => {
  const response = await axios.post(`${API_URL}agent/storeTagnames`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

export const editTagnames = async (input) => {
  const response = await axios.post(`${API_URL}agent/editTagnames`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

export const createTagDetails = async (input) => {
  const response = await axios.post(`${API_URL}agent/createTagDetails`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

export const getusertagDetails = async (id) => {
  const response = await axios.get(
    `${API_URL}agent/getusertagDetails/${id}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getTagDetails = async (id) => {
  const response = await axios.get(
    `${API_URL}agent/getTagDetails/${id}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getBuyerContactSolicitor = async () => {
  const response = await axios.get(
    `${API_URL}buyer/getBuyerContact/Solicitor/0/10`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getVendorContactSolicitor = async () => {
  const response = await axios.get(
    `${API_URL}vendor/getVendorContact/Solicitor/0/10`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getBuyerContactAgent = async () => {
  const response = await axios.get(
    `${API_URL}buyer/getBuyerContact/Agent/0/10`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getVendorContactAgent = async () => {
  const response = await axios.get(
    `${API_URL}vendor/getVendorContact/Agent/0/10`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getVendorContact = async (userType) => {
  const response = await axios.get(
    `${API_URL}vendor/getVendorContact/All/0/10`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const getBuyerContact = async (userType) => {
  const response = await axios.get(
    `${API_URL}buyer/getBuyerContact/All/0/10`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

export const request66W = async (input) => {
  const response = await axios.post(`${API_URL}buyer/request66W`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

export const forwardContract = async (input) => {
  const response = await axios.post(`${API_URL}buyer/forwardContract`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

export const getOwnerCardActivity = async (proid, ownerId, marketShow) => {
  const id = marketShow ? 0 : proid;
  const response = await axios.get(
    `${API_URL}agent/ownerCardActivity/${id}/${ownerId}/Default`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("getOwnerCardActivity", response.data);

  return response.data;
};

export const addNote = async (input) => {
  const response = await axios.post(`${API_URL}user/addNote`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  return response.data;
};

export const getNotes = async ({
  receiverType,
  receiverId,
  filter = 'property',
  propertyId,
  userType,
  userId,
}) => {
  const finalReceiverType = receiverType || userType;
  const finalReceiverId = receiverId || userId;
  const response = await axios.get(
    `${API_URL}user/notes?receiverType=${finalReceiverType}&receiverId=${finalReceiverId}&filter=${filter}&propertyId=${propertyId}`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

const getChecklistMaster = async (propertyId, currentUrl, primaryUserType) => {
  if (currentUrl?.includes('share') && primaryUserType != 'Buyer') {
    const response = await axios.get(
      `${API_URL}buyer/listTasksShare/` + `${propertyId}`,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getChecklistMaster", response.data);

    return response.data;
  } else {
    const response = await axios.get(
      `${API_URL}buyer/listTasks/` + `${propertyId}`,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getChecklistMaster", response.data);

    return response.data;
  }
};
const getlistScheduledInspection = async (
  propertyId,
  currentUrl,
  primaryUserType
) => {
  if (currentUrl?.includes('share') && primaryUserType != 'Buyer') {
    const url =
      `${API_URL}buyer/listScheduledInspectionShare/` + `${propertyId}`;
    const response = await axios.get(
      url,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getlistScheduledInspection", response.data);

    return response.data;
  } else {
    const url = `${API_URL}buyer/listScheduledInspection/` + `${propertyId}`;
    const response = await axios.get(
      url,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getlistScheduledInspection", response.data);

    return response.data;
  }
};

export const getlistPrivateInspection = async (
  propertyId,
  currentUrl,
  primaryUserType
) => {
  if (currentUrl?.includes('share') && primaryUserType != 'Buyer') {
    const url = `${API_URL}buyer/listPrivateInspectionShare/` + `${propertyId}`;
    const response = await axios.get(
      url,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getlistScheduledInspection", response.data);

    return response.data;
  } else {
    const url = `${API_URL}buyer/listPrivateInspection/` + `${propertyId}`;
    const response = await axios.get(
      url,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getlistScheduledInspection", response.data);

    return response.data;
  }
};

const getlistMakeOffer = async (propertyId, currentUrl, primaryUserType) => {
  if (currentUrl?.includes('share') && primaryUserType != 'Buyer') {
    const response = await axios.get(
      `${API_URL}buyer/listMakeOfferShare/` + `${propertyId}`,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getlistMakeOffer", response.data);

    return response.data;
  } else {
    const response = await axios.get(
      `${API_URL}buyer/listMakeOffer/` + `${propertyId}`,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getlistMakeOffer", response.data);

    return response.data;
  }
};

const scheduleInspection = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "input");
  const response = await axios.post(
    `${API_URL}buyer/scheduleInspection`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("scheduleInspection", response.data);

  return response.data;
};
const viewContract = async (propertyId) => {
  const response = await axios.get(
    `${API_URL}buyer/viewContract/` + `${propertyId}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("viewContract", response.data);

  return response.data;
};
const makeOfferRequest = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "input");
  const response = await axios.post(`${API_URL}buyer/makeOffer`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("makeOfferRequest", response.data);

  return response.data;
};

const getBuyerDashboardOffer = async (page, pageSize) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  const offset = (page - 1) * pageSize;
  // console.log(input, "input");
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/Offer/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};
const getBuyerDashboardInspected = async (page, pageSize) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);
  const offset = (page - 1) * pageSize;

  // console.log(input, "input");
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/Inspected/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("getBuyerDashboardInspected", response.data);

  return response.data;
};

const getBuyerDashboardInterested = async (page, pageSize) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  const offset = (page - 1) * pageSize;
  // console.log(input, "input");
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/Interested/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("getBuyerDashboardInterested", response.data);

  return response?.data;
};
const getBuyerDashboardWatched = async (page, pageSize) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  const offset = (page - 1) * pageSize;
  // console.log(input, "input");
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/Watching/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("getBuyerDashboardWatched", response.data);

  return response.data;
};

const getBuyerDashboardViewedProperties = async (page, pageSize) => {
  const offset = (page - 1) * pageSize;
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/ViewedProperties/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

const getBuyerDashboardArchived = async (page, pageSize) => {
  const offset = (page - 1) * pageSize;
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/Archived/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

const getBuyerDashboardExchanged = async (page, pageSize) => {
  const offset = (page - 1) * pageSize;
  const response = await axios.get(
    `${API_URL}buyer/getPropertyList/Exchanged/${offset}/${pageSize}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  return response.data;
};

const buyerCheckIn = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(`${API_URL}agent/getQRValue`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("buyerCheckIn", response.data);

  return response.data;
};

const buyerCounterOffer = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(`${API_URL}vendor/vendorAction`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("buyerCounterOffer", response.data);

  return response.data;
};
export const depositPayment = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(`${API_URL}buyer/approveDeposit`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("buyerCounterOffer", response.data);

  return response.data;
};

const forwardOfferToVendor = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(
    `${API_URL}agent/forwardedOfferToVendor`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("forwardOfferToVendor", response.data);

  return response.data;
};
const forwardOfferToBuyer = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(
    `${API_URL}agent/forwardedOfferToBuyer`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("forwardOfferToBuyer", response.data);

  return response.data;
};

const forwardBuyersOfferToOtherBuyers = async (input) => {
  // console.log(input, "inputQrrrr");
  const response = await axios.post(
    `${API_URL}agent/forwardedBuyerOfferToBuyer`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("forwardBuyersOfferToOtherBuyers", response.data);

  return response.data;
};

const forwardBuyeractiontoVendor = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(
    `${API_URL}agent/forwardedBuyerActionToVendor`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("forwardBuyeractiontoVendor", response.data);

  return response.data;
};

const confirmBuyerOffer = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(`${API_URL}buyer/buyerAction`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("confirmBuyerOffer", response.data);

  return response.data;
};
const stageTwoSwitching = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(`${API_URL}agent/stageTwo`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });

  // console.log("stageTwoSwitching", response.data);

  return response.data;
};
const getDueDelligenceFile = async (input, currentUrl, primaryUserType) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  if (currentUrl?.includes('share') && primaryUserType != 'Buyer') {
    const response = await axios.get(
      `${API_URL}buyer/dueDiligenceShare/${input}`,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getDueDelligenceFile", response.data);

    return response.data;
  } else {
    const response = await axios.get(
      `${API_URL}buyer/dueDiligence/${input}`,

      {
        headers: {
          Authorization: localStorage.getItem('accesstoken'),
        },
      }
    );

    // console.log("getDueDelligenceFile", response.data);

    return response.data;
  }
};
const flagDueDelligenceFile = async (propertyId, type) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(propertyId, type, "inputQr");
  const response = await axios.get(
    `${API_URL}buyer/dueLink/${propertyId}/${type}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("flagDueDelligenceFile", response.data);

  return response.data;
};
export const documentTypeList = async (input) => {
  const response = await axios.get(
    `${API_URL}documentTypeList/${input}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );
  return response.data;
};

export const pipelineDocumentTypeList = async (tempId, propertyId) => {
  const response = await axios.get(
    `${API_URL}pipelineDocumentTypeList/${tempId}/${propertyId}`,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );
  return response.data;
};

const addBuyerToStagingTwo = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(
    `${API_URL}agent/addBuyertoStageTwo`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("addBuyerToStagingTwo", response.data);

  return response.data;
};

const addInspectionFunction = async (input) => {
  // const token = process.env.REACT_APP_apiToken;
  // console.log(`token: ${token}`);

  // console.log(input, "inputQr");
  const response = await axios.post(
    `${API_URL}agent/addInspectionDetails`,
    input,
    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("addInspectionFunction", response.data);

  return response.data;
};

const replyToVendorStatus = async (input) => {
  const response = await axios.post(`${API_URL}agent/replyToVendor`, input, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

const getForecastData = async (propertyId) => {
  const response = await axios.get(
    `${API_URL}agent/forecast/` + `${propertyId}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("getForecastData", response.data);

  return response.data;
};
const getUpcomingInspection = async (tempId) => {
  const response = await axios.get(
    `${API_URL}agent/getUpcomingInspection/` + `${tempId}`,

    {
      headers: {
        Authorization: localStorage.getItem('accesstoken'),
      },
    }
  );

  // console.log("getForecastData", response.data);

  return response.data;
};

const Moxoseparationinlisting = async (url, data) => {
  let params = { requestData: data };
  const response = await axios.post(`${API_URL}moxo/` + `${url}`, params, {
    headers: {
      Authorization: localStorage.getItem('accesstoken'),
    },
  });
  return response.data;
};

export {
  getForecastData,
  getApiCall,
  postApiCall,
  putApiCall,
  deleteApiCall,
  axiosconfig,
  getContactDetails,
  getBuyerDetails,
  getOwnerDetails,
  getCount,
  getBuyerIndividualData,
  getFollowRequest,
  getChecklistMaster,
  getlistScheduledInspection,
  getlistMakeOffer,
  scheduleInspection,
  viewContract,
  makeOfferRequest,
  getBuyerDashboardOffer,
  getBuyerDashboardInspected,
  getBuyerDashboardWatched,
  getBuyerDashboardArchived,
  getBuyerDashboardExchanged,
  getBuyerDashboardViewedProperties,
  buyerCheckIn,
  buyerCounterOffer,
  forwardOfferToVendor,
  forwardOfferToBuyer,
  confirmBuyerOffer,
  stageTwoSwitching,
  getDueDelligenceFile,
  flagDueDelligenceFile,
  addBuyerToStagingTwo,
  getBuyerDashboardInterested,
  addInspectionFunction,
  getUpcomingInspection,
  forwardBuyeractiontoVendor,
  forwardBuyersOfferToOtherBuyers,
  replyToVendorStatus,
  Moxoseparationinlisting,
};
